import React, {forwardRef} from 'react'
import styled, {css} from 'styled-components'
import sx, {SxProp} from '@fuegokit/react/sx'
import Box from '@fuegokit/react/components/Box'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import themeGet, {FuegokitThemeMapType} from '@fuegokit/react/utils/theme-get'
import Text from '@fuegokit/react/components/Text'
import {Appearance} from './types'

export interface AtlasLozengeProps {
  /**
   * Elements to be rendered inside the lozenge. Ideally no more than two words.
   */
  children?: React.ReactNode
  /**
   * The appearance type.
   */
  appearance?: Appearance
  /**
   * Determines whether to apply the bold style or not.
   */
  isBold?: boolean
  /**
   * max-width of lozenge container. Default to 200px.
   */
  maxWidth?: number | string
  /**
   * Truncates text with an ellipsis when text overflows its parent container
   * (i.e. `width` has been set on parent that is shorter than text length).
   */
  shouldTruncate?: boolean
  /**
   * A `testId` prop is provided for specified elements, which is a unique
   * string that appears as a data attribute `data-testid` in the rendered code,
   * serving as a hook for automated tests
   */
  testId?: string
}

const labelAppearanceObject: {bold: FuegokitThemeMapType; default: FuegokitThemeMapType} = {
  bold: {
    default: 'colors.text.inverse',
    inprogress: 'colors.text.inverse',
    moved: 'colors.text.warning.inverse',
    new: 'colors.text.inverse',
    removed: 'colors.text.inverse',
    success: 'colors.text.inverse',
    todo: 'colors.text.inverse',
    any: 'colors.text.inverse',
    done: 'colors.text.inverse',
    locked: 'colors.text.disabled'
  },
  default: {
    default: 'colors.text.subtle',
    inprogress: 'colors.text.information',
    moved: 'colors.text.warning.default',
    new: 'colors.text.discovery',
    removed: 'colors.text.danger',
    success: 'colors.text.success',
    todo: 'colors.text.subtle',
    any: 'colors.text.subtle',
    done: 'colors.text.success',
    locked: 'colors.text.disabled'
  }
}

const wrapperAppearanceObject: {bold: FuegokitThemeMapType; default: FuegokitThemeMapType} = {
  bold: {
    default: 'colors.background.neutral.bold.default',
    inprogress: 'colors.background.information.bold.default',
    moved: 'colors.background.warning.bold.default',
    new: 'colors.background.discovery.bold.default',
    removed: 'colors.background.danger.bold.default',
    success: 'colors.background.success.bold.default',
    todo: 'colors.background.neutral.bold.default',
    any: 'colors.background.neutral.bold.default',
    done: 'colors.background.success.bold.default',
    locked: 'colors.background.disabled'
  },
  default: {
    default: 'colors.background.neutral.default.[default]',
    inprogress: 'colors.background.information.default.[default]',
    moved: 'colors.background.warning.default.[default]',
    new: 'colors.background.discovery.default.[default]',
    removed: 'colors.background.danger.default.[default]',
    success: 'colors.background.success.default.[default]',
    todo: 'colors.background.neutral.default.[default]',
    any: 'colors.background.neutral.default.[default]',
    done: 'colors.background.success.default.[default]',
    locked: 'colors.background.disabled'
  }
}

// @TODO: create a proper color map once we know all statuses
const Wrapper = styled(Box)<SxProp & AtlasLozengeProps>`
  box-sizing: border-box;
  display: inline-flex;
  border-radius: ${themeGet('radii.2')};
  padding-inline: ${themeGet('space.1')};
  position: static;
  overflow: hidden;
  background-color: ${p => {
    if (p.appearance && {}.hasOwnProperty.call(wrapperAppearanceObject.bold, p.appearance) && p.isBold)
      return themeGet(wrapperAppearanceObject.bold[p.appearance])
    if (p.appearance && {}.hasOwnProperty.call(wrapperAppearanceObject.bold, p.appearance))
      return themeGet(wrapperAppearanceObject.default[p.appearance])
    return 'revert'
  }};
  ${sx}
`
const Label = styled(Text)<SxProp & AtlasLozengeProps>`
  font-weight: bold;
  font-size: ${themeGet('fontSizes.1')};
  line-height: ${themeGet('lineHeights.default')};
  text-transform: uppercase;
  color: ${p => {
    if (p.appearance && {}.hasOwnProperty.call(labelAppearanceObject.bold, p.appearance) && p.isBold)
      return themeGet(labelAppearanceObject.bold[p.appearance])
    if (p.appearance && {}.hasOwnProperty.call(labelAppearanceObject.bold, p.appearance))
      return themeGet(labelAppearanceObject.default[p.appearance])
    return 'revert'
  }};
  //
  ${p =>
    p.shouldTruncate &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
`

/**
 *
 * Visual indicator used to highlight an item's status for quick recognition.
 *
 * Includes additional custom statuses and can be used as a drop-in replacement in Atlassian products.
 *
 */
const AtlasLozenge = forwardRef(
  (
    {
      children,
      testId,
      isBold = false,
      appearance = 'default',
      shouldTruncate = true,
      maxWidth = 200,
      ...props
    }: AtlasLozengeProps,
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLSpanElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)

    const appearanceStyle = isBold ? 'bold' : 'subtle'
    const maxWidthValue = typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`
    const maxWidthIsPc = typeof maxWidth === 'string' && /%$/.test(maxWidth)

    return (
      <Wrapper
        as="span"
        appearance={appearance}
        isBold={isBold}
        ref={innerRef}
        sx={{maxWidth: maxWidthIsPc ? maxWidth : '100%'}}
        testId={testId}
        {...props}
      >
        <Label
          appearance={appearance}
          shouldTruncate={shouldTruncate}
          isBold={isBold}
          sx={{maxWidth: maxWidthIsPc ? '100%' : `calc(${maxWidthValue} - 8px)`}}
          data-testid={testId && `${testId}--text`}
        >
          {children}
        </Label>
      </Wrapper>
    )
  }
)

AtlasLozenge.displayName = 'AtlasLozenge'
export default AtlasLozenge
